import { useEffect, useRef } from "react";

interface AnimatedBarProps {
  x: number;
  width: number;
  /** Final y position. */
  targetY: number;
  /** Final height. */
  targetHeight: number;
  /**
   * The y value at which the bar is "zero height". For bottom-anchored bar
   * charts this is `innerH` (the plot's baseline). Passed explicitly so the
   * component doesn't have to know about chart layout.
   */
  baselineY: number;
  fill: string;
  rx?: number;
  delayMs?: number;
  durationMs?: number;
  onMouseMove?: (e: React.MouseEvent<SVGRectElement>) => void;
  onMouseLeave?: () => void;
}

/**
 * Bar `<rect>` that animates its SVG `y` and `height` attributes directly
 * via `setAttribute` and `requestAnimationFrame`.
 *
 * On mount: y tweens from `baselineY` to `targetY`, height from 0 to
 * `targetHeight` — reads as "growing up from the baseline".
 *
 * On prop change: tweens from the currently-displayed values to the new
 * targets (not restart from 0), so filter-driven updates glide.
 *
 * We're not using framer-motion's `motion.rect` with `animate={{y, height}}`
 * here because framer v11+ routes those props through CSS transform +
 * `style.height`, not through SVG attributes. Setting the attributes
 * directly is portable across browsers and unambiguous.
 */
export function AnimatedBar({
  x,
  width,
  targetY,
  targetHeight,
  baselineY,
  fill,
  rx,
  delayMs = 0,
  durationMs = 600,
  onMouseMove,
  onMouseLeave,
}: AnimatedBarProps) {
  const ref = useRef<SVGRectElement>(null);
  const prevYRef = useRef<number>(baselineY);
  const prevHRef = useRef<number>(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const fromY = prevYRef.current;
    const fromH = prevHRef.current;

    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      prevYRef.current = targetY;
      prevHRef.current = targetHeight;
      el.setAttribute("y", String(targetY));
      el.setAttribute("height", String(Math.max(0, targetHeight)));
      return;
    }

    if (Math.abs(fromY - targetY) < 0.1 && Math.abs(fromH - targetHeight) < 0.1) {
      return;
    }

    let startTime: number | null = null;
    let rafId = 0;
    const ease = (t: number) => 1 - Math.pow(1 - t, 3); // easeOutCubic

    const step = (now: number) => {
      if (startTime === null) startTime = now;
      const elapsed = now - startTime - delayMs;
      if (elapsed < 0) {
        rafId = requestAnimationFrame(step);
        return;
      }
      const t = Math.min(1, elapsed / durationMs);
      const eased = ease(t);
      const currentY = fromY + (targetY - fromY) * eased;
      const currentH = fromH + (targetHeight - fromH) * eased;
      prevYRef.current = currentY;
      prevHRef.current = currentH;
      el.setAttribute("y", String(currentY));
      el.setAttribute("height", String(Math.max(0, currentH)));
      if (t < 1) rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [targetY, targetHeight, delayMs, durationMs]);

  return (
    <rect
      ref={ref}
      x={x}
      y={baselineY}
      width={width}
      height={0}
      fill={fill}
      rx={rx}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    />
  );
}
